import React from 'react';
import { ImageIcon } from '@revfluence/fresh-icons/regular/esm';
import { InstagramIcon, TiktokIcon, YoutubeIcon } from '@revfluence/fresh-icons/brands/esm';
import { Tooltip, TooltipProvider, TooltipTrigger } from '@frontend/shadcn/components/ui/tooltip';
import {
  TooltipHeader,
  TooltipTitle,
  TooltipBody,
  TooltipItem,
  TooltipItemValue,
  TooltipArrow,
  TooltipContent,
} from '../tooltip';
import { useSocialMediaThumbnail } from '../../hooks/useSocialMediaThumbnail';
import { ContentTypeEnum } from '../../utils/getSocialMediaThumbnail';

interface SocialMediaThumbnailProps {
  postImageUrl?: string;
  postUrl: string;
  memberName: string;
  entityType: string;
  entityNetwork: string;
  width?: number;
  height?: number;
  className?: string;
}

const PlatformIcon = ({ type, className }: { type: ContentTypeEnum | null; className?: string }) => {
  switch (type) {
    case ContentTypeEnum.INSTAGRAM:
    case ContentTypeEnum.INSTAGRAM_REEL:
      return <InstagramIcon className={className} />;
    case ContentTypeEnum.TIKTOK:
      return <TiktokIcon className={className} />;
    case ContentTypeEnum.YOUTUBE:
    case ContentTypeEnum.YOUTUBE_SHORT:
      return <YoutubeIcon className={className} />;
    case ContentTypeEnum.IMAGE:
      return <ImageIcon className={className} />;
    default:
      return <ImageIcon className={className} />;
  }
};

export const isContentTypeEnum = (value: unknown): value is ContentTypeEnum => {
  return typeof value === 'string' && Object.values(ContentTypeEnum).includes(value as ContentTypeEnum);
};

export const getContentTypeLabel = (type: ContentTypeEnum | null): string => {
  switch (type) {
    case ContentTypeEnum.INSTAGRAM:
      return 'Instagram post';
    case ContentTypeEnum.INSTAGRAM_REEL:
      return 'Instagram reel';
    case ContentTypeEnum.YOUTUBE:
      return 'YouTube video';
    case ContentTypeEnum.YOUTUBE_SHORT:
      return 'YouTube short';
    case ContentTypeEnum.TIKTOK:
      return 'TikTok video';
    case ContentTypeEnum.FACEBOOK:
      return 'Facebook post';
    case ContentTypeEnum.PINTEREST:
      return 'Pinterest pin';
    case ContentTypeEnum.IMAGE:
      return 'Image';
    default:
      return 'Content';
  }
};

const ThumbnailTooltip: React.FC<{
  contentType: ContentTypeEnum | null;
  title?: string;
  error?: string | null;
  children: React.ReactNode;
}> = ({ contentType, title, error, children }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger className="block h-6">{children}</TooltipTrigger>
      <TooltipContent>
        <TooltipHeader>
          <TooltipTitle>{title || getContentTypeLabel(contentType)}</TooltipTitle>
        </TooltipHeader>
        <TooltipBody>
          <TooltipItem>
            <TooltipItemValue>
              {error || `Click to view ${getContentTypeLabel(contentType).toLowerCase()}`}
            </TooltipItemValue>
          </TooltipItem>
        </TooltipBody>
        <TooltipArrow />
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const IconPlaceholder: React.FC<{
  contentType: ContentTypeEnum | null;
}> = ({ contentType }) => (
  <PlatformIcon type={contentType} className="h-6 w-6 inline-block leading-none text-gray-400" />
);

const ThumbnailImage: React.FC<{
  src: string;
  alt: string;
  className?: string;
  style: React.CSSProperties;
  onLoad?: () => void;
  onError?: () => void;
}> = ({ src, alt, className, style, onLoad, onError }) => (
  <img src={src} alt={alt} className={className} style={style} onLoad={onLoad} onError={onError} />
);

export const SocialMediaThumbnail: React.FC<SocialMediaThumbnailProps> = ({
  postImageUrl,
  postUrl,
  memberName,
  entityType,
  entityNetwork,
  width = 24,
  height = 24,
  className,
}) => {
  const { imageUrl, error, contentType, title, onImageError } = useSocialMediaThumbnail({
    postImageUrl,
    postUrl,
  });

  const containerStyle = {
    width,
    height,
  };

  const shouldShowIcon = error || !imageUrl;

  return (
    <ThumbnailTooltip contentType={contentType} title={title} error={error}>
      {shouldShowIcon ? (
        <IconPlaceholder contentType={contentType} />
      ) : (
        imageUrl && (
          <ThumbnailImage
            src={imageUrl}
            alt={`${memberName}'s ${entityType} on ${entityNetwork}`}
            className={className}
            style={containerStyle}
            onError={onImageError}
          />
        )
      )}
    </ThumbnailTooltip>
  );
};
